import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import PageLayout from '../components/PageLayout';
import { CONFIG } from '../config/config';
import { useUser } from '../context/UserContext';
import { ArrowLeft, Send, Download } from 'lucide-react';

// 1. Transformation en tableau d'objets avec correction des accents
const MOTIFS_FREQUENTS = [
  { motif: "Angine", poids: 100 },
  { motif: "Syndrome infectieux broncho-pulmonaire", poids: 100 },
  { motif: "Gastro-entérite virale", poids: 100 },
  { motif: "Grippe saisonnière", poids: 100 },
  { motif: "COVID", poids: 100 },
  { motif: "Lombalgie commune", poids: 100 },
  { motif: "Cervicalgie non spécifique", poids: 100 },
  { motif: "Sciatique", poids: 100 },
  { motif: "Troubles anxio-dépressifs mineurs", poids: 100 },
  { motif: "Entorse de la cheville", poids: 50 },
  { motif: "Entorse du genou", poids: 50 },
  { motif: "Fracture du membre inférieur", poids: 50 },
  { motif: "Fracture du membre supérieur", poids: 50 },
  { motif: "Amygdalectomie", poids: 10 },
  { motif: "Appendicectomie", poids: 10 },
  { motif: "Arthroplastie de la hanche par prothèse totale", poids: 10 },
  { motif: "Arthroplastie du genou par prothèse totale", poids: 10 },
  { motif: "Chirurgie de la cataracte", poids: 10 },
  { motif: "Chirurgie de l'obésité morbide", poids: 10 },
  { motif: "Chirurgie des varices (crossectomie-éveinage)", poids: 10 },
  { motif: "Cholécystectomie", poids: 10 },
  { motif: "Conisation du col de l'utérus", poids: 10 },
  { motif: "Cure de hernie de la paroi abdominale", poids: 10 },
  { motif: "Cure de hernie discale par discectomie", poids: 10 },
  { motif: "Cure de hernie inguinale", poids: 10 },
  { motif: "Hémorroïdes : traitement chirurgical", poids: 10 },
  { motif: "Hystérectomie", poids: 10 },
  { motif: "Kystectomie ovarienne / ovariectomie", poids: 10 },
  { motif: "Lésions anales : traitement chirurgical", poids: 10 },
  { motif: "Lésions méniscales : traitement chirurgical", poids: 10 },
  { motif: "Libération du nerf ulnaire au coude", poids: 10 },
  { motif: "Ligamentoplastie du croisé antérieur du genou", poids: 10 },
  { motif: "Mastoplastie de réduction", poids: 10 },
  { motif: "Ostéotomie pour hallux valgus", poids: 10 },
  { motif: "Plaies des tendons extenseurs et fléchisseurs de la main", poids: 10 },
  { motif: "Plaies profondes de la main et des doigts", poids: 10 },
  { motif: "Rupture du tendon d'Achille : traitement chirurgical", poids: 10 },
  { motif: "Septoplastie nasale", poids: 10 },
  { motif: "Sinus pilonidal : traitement chirurgical", poids: 10 },
  { motif: "Ténosynovectomie des muscles de la main", poids: 10 },
  { motif: "Thyroïdectomie", poids: 10 },
  { motif: "Infarctus du myocarde", poids: 10 },
  { motif: "Revascularisation coronarienne", poids: 10 },
  { motif: "Sinusite maxillaire", poids: 10 },
  { motif: "Rupture des tendons de la coiffe des rotateurs", poids: 10 },
  { motif: "Syndrome du canal carpien", poids: 10 },
  { motif: "Tendinopathie de la coiffe des rotateurs", poids: 10 }
];

// 2. Fonction de tri (Poids décroissant, puis alphabétique)
MOTIFS_FREQUENTS.sort((a, b) => {
  if (b.poids !== a.poids) {
    return b.poids - a.poids; 
  }
  return a.motif.localeCompare(b.motif, 'fr');
});

const MOTIFS_NO_TELETRANS_MAP = {
  tiers: 'Patient couvert par une institution refusant la prise en charge par la Sécurité Sociale',
  refus: 'Refus du patient de télétransmettre',
  non_reconnu: 'Patient avec numéro de Sécurité Sociale non reconnu pour télétransmission',
  sans_secu: 'Patient sans numéro de Sécurité Sociale française',
  erreur_mus: 'Ne pas remplir si inconnu',
};

const MOTIFS_NO_TELETRANS_LIST = [
  'Patient sans numéro de Sécurité Sociale française',
  'Patient couvert par une institution refusant la prise en charge par la Sécurité Sociale',
  'Patient avec numéro de Sécurité Sociale non reconnu pour télétransmission',
  'Refus du patient de télétransmettre',
  'Ne pas remplir si inconnu'
];

// --- Dictionnaires pour la conversion des dates en toutes lettres ---
const JOURS_LETTRES = {
  1: "premier", 2: "deux", 3: "trois", 4: "quatre", 5: "cinq",
  6: "six", 7: "sept", 8: "huit", 9: "neuf", 10: "dix",
  11: "onze", 12: "douze", 13: "treize", 14: "quatorze", 15: "quinze",
  16: "seize", 17: "dix-sept", 18: "dix-huit", 19: "dix-neuf", 20: "vingt",
  21: "vingt et un", 22: "vingt-deux", 23: "vingt-trois", 24: "vingt-quatre", 25: "vingt-cinq",
  26: "vingt-six", 27: "vingt-sept", 28: "vingt-huit", 29: "vingt-neuf", 30: "trente",
  31: "trente et un"
};

const MOIS_LETTRES = {
  1: "janvier", 2: "février", 3: "mars", 4: "avril", 5: "mai", 6: "juin",
  7: "juillet", 8: "août", 9: "septembre", 10: "octobre", 11: "novembre", 12: "décembre"
};

const decouperAdresse = (adresseComplete) => {
  if (!adresseComplete) return { rue: '', cp: '', ville: '' };
  
  // On cherche 5 chiffres à la suite (le code postal)
  const cpMatch = adresseComplete.match(/\b\d{5}\b/);
  
  if (cpMatch) {
    const cp = cpMatch[0];
    const indexCp = cpMatch.index;
    
    // La rue : tout ce qui est avant le code postal (en enlevant la virgule s'il y en a une)
    const rue = adresseComplete.substring(0, indexCp).replace(/,\s*$/, '').trim();
    
    // La ville : tout ce qui est après le code postal
    const ville = adresseComplete.substring(indexCp + cp.length).trim();
    
    return { rue, cp, ville };
  }
  
  return { rue: adresseComplete, cp: '', ville: '' };
};

const nombreEnLettres = (n) => {
  if (n <= 16) return ["", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf", "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize"][n];
  if (n < 20) return "dix-" + nombreEnLettres(n - 10);
  if (n < 70) {
    const dizaines = ["", "", "vingt", "trente", "quarante", "cinquante", "soixante"];
    const d = Math.floor(n / 10);
    const u = n % 10;
    if (u === 0) return dizaines[d];
    if (u === 1) return dizaines[d] + " et un";
    return dizaines[d] + "-" + nombreEnLettres(u);
  }
  if (n < 80) return n === 71 ? "soixante et onze" : "soixante-" + nombreEnLettres(n - 60);
  if (n < 90) return n === 80 ? "quatre-vingts" : "quatre-vingt-" + nombreEnLettres(n - 80);
  if (n < 100) return "quatre-vingt-" + nombreEnLettres(n - 80);
  return "";
};

const anneeEnLettres = (anneeStr) => {
  const annee = parseInt(anneeStr, 10);
  if (annee >= 2000 && annee <= 2099) {
    const reste = annee % 100;
    return reste === 0 ? "deux mille" : "deux mille " + nombreEnLettres(reste);
  }
  return anneeStr; 
};

const dateEnLettres = (dateString) => {
  if (!dateString) return "";
  const [y, m, d] = dateString.split('-');
  const jour = JOURS_LETTRES[parseInt(d, 10)] || d;
  const mois = MOIS_LETTRES[parseInt(m, 10)] || m;
  const annee = anneeEnLettres(y);
  return `${jour} ${mois} ${annee}`.toUpperCase();
};

export default function FormulaireArretTravail({ mode = 'pdf' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const { profile, etablissement } = useUser();
  const [searchParams] = useSearchParams();

  const [patient, setPatient] = useState(null);
  const [paramsAT, setParamsAT] = useState(null); 
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showMotifs, setShowMotifs] = useState(false);
  const [showMotifsNoTeletrans, setShowMotifsNoTeletrans] = useState(false);

  const MEDECINS = paramsAT?.medecins || {};
  const STRUCTURES = paramsAT?.structures || {};

  const motifFromTree = MOTIFS_NO_TELETRANS_MAP[searchParams.get('motifNoTeletrans')] || 'Patient sans numéro de Sécurité Sociale';

  const [form, setForm] = useState({
    typeArret: 'initial',
    nom: '',
    prenom: '',
    numSecu: 'PAS UN ASSURÉ SOCIAL CPAM',
    motifNoTeletrans: motifFromTree,
    adresseRue: '',     
    adresseCp: '',      
    adresseVille: '',       
    telephone: '',
    nature: 'tempsComplet',
    motif: '',
    dateFin: new Date().toISOString().split('T')[0],
    affection: 'non',
    grossesse: 'non',
    sortiesLimite: 'oui',
    sortiesoui: new Date().toISOString().split('T')[0],
    sortiesLibre: 'non',
    sortiesLibreDate: new Date().toISOString().split('T')[0],
    dateDebutTempsPartiel: new Date().toISOString().split('T')[0],
    dateFinTempsPartiel: new Date().toISOString().split('T')[0],
    affectionTempsPartiel: 'non',
    doc: '',
    rpps: '',
    idStructure: '', // Rempli après le load
    numStructure: '', // Rempli après le load
    dateDuJour: new Date().toISOString().split('T')[0],
  });

  const motifsFiltres = MOTIFS_FREQUENTS.filter(m => 
    m.motif.toLowerCase().includes((form.motif || '').toLowerCase())
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch patient et configuration en même temps
        const [resPatient, resParams] = await Promise.all([
          fetch(`${CONFIG.API_URL}/passages/${id}`, { headers }),
          fetch(`${CONFIG.API_URL}/arret-travail/params-at`, { headers })
        ]);

        if (!resPatient.ok) throw new Error('Patient introuvable');
        
        const data = await resPatient.json();
        const paramsData = await resParams.json();
        
        setPatient(data);
        setParamsAT(paramsData);

        const adresseComplete = data.adressePostale || data.adresse_postale || data.adresse || '';
        const { rue, cp, ville } = decouperAdresse(adresseComplete);

        const defaultDoc = profile?.profile_nom ? `Dr ${profile.profile_prenom} ${profile.profile_nom}`.trim() : '';

        const trigramme = (
          etablissement?.etablissement_trigramme ||
          profile?.initEtab?.etablissement_trigramme ||
          data?.etablissement_trigramme ||
          'AUT'
        ).toUpperCase();

        const structEntry = Object.entries(paramsData.structures).find(([, s]) =>
          s.trigrammes.some(t => trigramme.includes(t))
        ) || Object.entries(paramsData.structures)[0];

        const [nomStructure, detailStructure] = structEntry;

        setForm(prev => ({
          ...prev,
          nom: data.nom || '',
          prenom: data.prenom || '',
          numSecu: data.numeroSecu || data.numero_secu || data.numSecu || data.nir || 'PAS UN ASSURÉ SOCIAL CPAM',
          adresseRue: rue,      
          adresseCp: cp,        
          adresseVille: ville,
          telephone: data.telephone || data.tel || data.numeroTelephone || data.mobile || '',  
          doc: paramsData.medecins[defaultDoc] ? defaultDoc : '',
          rpps: paramsData.medecins[defaultDoc] || '',
          idStructure: nomStructure,
          numStructure: detailStructure.num,
        }));

      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, token, profile, etablissement]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === 'doc') {
        updated.rpps = MEDECINS[value] || '';
      }
      if (name === 'idStructure') {
        updated.numStructure = STRUCTURES[value]?.num || '';
      }
      return updated;
    });
  };

  const sendSecretaryToDo = async ({ comment, urgence = false }) => {
    let etablissementId =
      etablissement?.etablissement_id ||
      profile?.profile_default_etablissement_id ||
      profile?.initEtab?.etablissement_id;

    if (!etablissementId) {
      throw new Error("Impossible de trouver l'identifiant de l'établissement (etablissementId).");
    }

    const rawDate = patient?.dateNaissance || patient?.ddn || patient?.date_naissance;
    let formattedDdn = '';
    try {
      if (rawDate) formattedDdn = new Date(rawDate).toISOString().split('T')[0];
    } catch {
      formattedDdn = String(rawDate || '');
    }

    // 1. On rassemble les 3 morceaux d'adresse
    const adresseComplete = `${form.adresseRue || ''} ${form.adresseCp || ''} ${form.adresseVille || ''}`.trim() || 'Non renseignée';

    const payload = {
      cibles: [{ role: 'secretaire', etablissementId }],
      profilDemandeurId: profile?.profile_id || null,
      passageId: id,
      passage_id: id,
      paramDefTodo: {
        key: 'Arret_Travail',
        name: 'Arrêt de travail',
        orderInMenu: 40,
        // 2. On remet la liste stricte d'origine pour éviter tout blocage backend
        entryToDoPII: ['nom', 'prenom', 'date_naissance', 'sexe'],
        entry: ['urgence', 'comment'],
        dropdowns: { sexe: ['H', 'F'] },
        lookupPassage_PII: [],
        lookupDB_Passage: [],
        lookupToDo_PII: ['nom', 'prenom', 'date_naissance'],
        autoField: ['dateCreationToDo', 'PrenomNomDDN'],
        displayOrder: ['PrenomNomDDN', 'sexe', 'comment'],
        copyIcon: ['PrenomNomDDN'],
      },
      urgence: Boolean(urgence),
      comment: comment || '',
      todo: {
        entryTodoPii: {
          nom: form.nom || patient?.nom || '',
          prenom: form.prenom || patient?.prenom || '',
          date_naissance: formattedDdn,
          sexe: patient?.sexe || 'F',
          // 3. On passe l'adresse reconstituée et les nouvelles valeurs
          adresse_postale: adresseComplete,
          telephone: form.telephone || 'Non renseigné',
          numero_secu: form.numSecu || 'Non renseigné',
        },
        entry: { urgence: Boolean(urgence), comment: comment || '' },
        entryTodo: { urgence: Boolean(urgence), comment: comment || '' },
        entryToDo: { urgence: Boolean(urgence), comment: comment || '' },
        urgence: Boolean(urgence),
        comment: comment || '',
      },
    };

    const res = await fetch(`${CONFIG.API_URL}/todos`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody.message || 'Erreur transmission To Do');
    }
  };
  const generateFilledPdf = async () => {
    try {
      const response = await window.fetch(`${window.location.origin}/Cerfa_10170.pdf`);
      if (!response.ok) throw new Error('Le fichier PDF vierge est introuvable.');

      const existingPdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const black = rgb(0, 0, 0);

      const allPages = pdfDoc.getPages();
      const targetPages = [];
      if (allPages.length > 1) targetPages.push(allPages[1]);
      if (allPages.length > 2) targetPages.push(allPages[2]);
      if (allPages.length > 3) targetPages.push(allPages[3]);
      if (targetPages.length === 0) targetPages.push(allPages[0]);

      const wrapText = (text, maxWidth, fontSize) => {
        const words = String(text).split(' ');
        const lines = [];
        let currentLine = '';
        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          if (font.widthOfTextAtSize(testLine, fontSize) <= maxWidth) {
            currentLine = testLine;
          } else {
            if (currentLine) lines.push(currentLine);
            currentLine = word;
          }
        }
        if (currentLine) lines.push(currentLine);
        return lines;
      };

      const drawOps = [
        { condition: form.typeArret === 'initial', text: 'X', pages: [1, 2, 3], x: 305, y: 804 },
        { condition: form.typeArret === 'prolongation', text: 'X', pages: [1, 2, 3], x: 362, y: 804 },

        { text: `${form.nom} ${form.prenom}`.trim(), pages: [1, 2, 3], x: 117, y: 720 },
        { text: `${form.nom} ${form.prenom}`.trim(), pages: [1, 2, 3], x: 296, y: 406 },

        { text: form.numSecu, pages: [1, 2, 3], x: 160, y: 736 },
        { text: form.motifNoTeletrans, pages: [1, 2, 3], x: 568, y: 690, maxWidth: 280, align: 'right' },
        { text: form.adresseRue, pages: [1, 2, 3], x: 568, y: 672, maxWidth: 230, align: 'right' }, 
        { text: form.adresseCp, pages: [1, 2, 3], x: 97, y: 653 },                
        { text: form.adresseVille, pages: [1, 2, 3], x: 213, y: 653},
        { text: form.telephone, pages: [1, 2, 3], x: 448, y: 653},

        { condition: form.nature === 'tempsComplet' && form.dateFin, type: 'date', value: form.dateFin, pages: [1, 2, 3], x: 271, y: 366 },
        { condition: form.nature === 'tempsComplet' && form.dateFin, text: dateEnLettres(form.dateFin), pages: [1, 2, 3], x: 278, y: 391 },
        
        { condition: form.nature === 'tempsComplet', text: 'X', pages: [1, 2], x: form.affection === 'oui' ? 170 : 96, y: 349 },
        { condition: form.nature === 'tempsComplet', text: 'X', pages: [1, 2, 3], x: form.grossesse === 'oui' ? 170 : 96, y: 332 },
        { condition: form.nature === 'tempsComplet', text: 'X', pages: [1, 2, 3], x: form.sortiesLimite === 'oui' ? 144 : 390, y: 302 },
        { condition: form.nature === 'tempsComplet' && form.sortiesLimite === 'oui' && form.sortiesoui, type: 'date', value: form.sortiesoui, pages: [1, 2, 3], x: 226, y: 300 },
        { condition: form.nature === 'tempsComplet', text: 'X', pages: [1, 2, 3], x: form.sortiesLibre === 'oui' ? 108 : 58, y: 265 },
        { condition: form.nature === 'tempsComplet' && form.sortiesLibre === 'oui' && form.sortiesLibreDate, type: 'date', value: form.sortiesLibreDate, pages: [1, 2, 3], x: 192, y: 263 },

        { condition: form.nature === 'tempsPartiel' && form.dateDebutTempsPartiel, type: 'date', value: form.dateDebutTempsPartiel, pages: [1, 2, 3], x: 259, y: 244 },
        { condition: form.nature === 'tempsPartiel' && form.dateFinTempsPartiel, type: 'date', value: form.dateFinTempsPartiel, pages: [1, 2, 3], x: 402, y: 244 },
        { condition: form.nature === 'tempsPartiel', text: 'X', pages: [1, 2], x: form.affectionTempsPartiel === 'oui' ? 170 : 96, y: 221 },

        { text: form.doc, pages: [1, 2, 3], x: 37, y: 107 },
        { text: form.rpps, pages: [1, 2, 3], x: 107, y: 87 },
        { type: 'date', value: form.dateDuJour, pages: [1, 2, 3], x: 66, y: 66 },

        { text: form.idStructure, pages: [1, 2, 3], x: 286, y: 111 },
        { text: form.numStructure, pages: [1, 2, 3], x: 372, y: 87 },

        { text: form.motif, pages: [1], x: 227, y: 166 }
      ];

      drawOps.forEach(op => {
        if (op.condition === false) return;

        let textToDraw = op.text || '';

        if (op.type === 'date' && op.value) {
          if (op.value === 'TODAY') {
            const d = new Date();
            textToDraw = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
          } else {
            const [y, m, d] = op.value.split('-');
            textToDraw = `${d}/${m}/${y}`;
          }
        }

        if (!textToDraw) return;

        if (textToDraw !== 'X') {
          textToDraw = String(textToDraw).toUpperCase().replace(/\u00A0/g, ' ').replace(/\n/g, ' ');
        }

        const size = op.size || 10;
        const lineHeight = size * 1.25;

        op.pages.forEach(pIdx => {
          if (pIdx - 1 >= targetPages.length) return;
          const page = targetPages[pIdx - 1];

          const lines = op.maxWidth ? wrapText(textToDraw, op.maxWidth, size) : [textToDraw];
          let currentY = op.y + ((lines.length - 1) * lineHeight);

          lines.forEach(line => {
            let finalX = op.x;
            if (op.align === 'right') {
              finalX = op.x - font.widthOfTextAtSize(line, size);
            }
            page.drawText(line, { x: finalX, y: currentY, size, font, color: black });
            currentY -= lineHeight;
          });
        });
      });

      const pdfBase64 = await pdfDoc.saveAsBase64(); // Retourne le base64 brut sans le préfixe dataUri
      return pdfBase64;

    } catch (error) {
      console.error('Erreur génération PDF :', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (mode === 'pdf') {
        // 1. Génère le PDF et récupère le fichier en Base64
        const base64Pdf = await generateFilledPdf();

        // 2. Détermination du site (AUT ou VHU)
        const trigramme = (
          etablissement?.etablissement_trigramme ||
          profile?.initEtab?.etablissement_trigramme ||
          patient?.etablissement_trigramme ||
          'AUT'
        ).toUpperCase();

        // 3. Préparation du contenu (convertir les \n en <br> pour le HTML)
        const destinataire = trigramme.includes('VHU')? "sec_vhu@monurgencesante.fr":"sec_aut@monurgencesante.fr";
        const htmlBody = `Bonjour,<br><br>Veuillez trouver ci-joint l'arrêt de travail de ${form.nom} ${form.prenom} à imprimer.<br>Merci de signaler au médecin lorsqu'il/elle peut venir le signer. <br><br>Cordialement, <br>${form.doc}`;
        const subject = `Arrêt de travail à imprimer - ${form.nom} ${form.prenom}`;
        const fileName = `Arret_Travail_${form.nom?.replace(/\s+/g, '_') || 'Patient'}.pdf`;

        // 4. Appel à ton API Backend pour envoyer le mail
        // RAPPEL : Assure-toi que cette route correspond à celle que tu vas créer dans ton backend Node.js
        const resMail = await fetch(`${CONFIG.API_URL}/passages/draft-arret`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            to: destinataire,
            subject: subject,
            htmlBody: htmlBody,
            attachmentBase64: base64Pdf,
            attachmentName: fileName
          }),
        });

        if (!resMail.ok) {
          throw new Error("Erreur lors de l'envoi de l'email automatique.");
        }

        // 5. Envoi de la To-Do secrétaire
        await sendSecretaryToDo({ comment: `Imprimer l'arrêt de travail envoyé par mail et demander au médecin de le signer.`, urgence: false });
        
        // 6. Alerte explicative pour l'utilisateur
        alert(`✅ L'arrêt de travail a été généré, envoyé par mail au secrétariat avec le PDF en pièce jointe. Merci d'aller signer l'arrêt de travail dans quelques minutes.`);
      } else {
        const recap = [
          `---[ARRÊT DE TRAVAIL PAPIER À COMPLÉTER]---`,
          `- Type : ${form.typeArret === 'initial' ? 'Initial' : 'Prolongation'}`,
          `\n ** Section "L'assuré" : ** `,
          `- N° d'immatriculation : ${form.numSecu}`,
          `- Nom et prénom : ${form.nom} ${form.prenom}`,
          `- Code de l'organisme de rattachement : ${form.motifNoTeletrans}`,
          `- Adresse : ${form.adresseRue}, ${form.adresseCp} ${form.adresseVille}`,
          `- Téléphone : ${form.telephone}`,
          `\n ** Section "Les renseignements médicaux" : ** `,
          form.nature === 'tempsComplet'
            ? `- Prescrit un arrêt de travail jusqu'au ${form.dateFin}`
            : `- Prescrit un temps partiel du ${form.dateDebutTempsPartiel} au ${form.dateFinTempsPartiel}`,
          `- Affection longue durée : ${form.affection === 'oui' ? 'En rapport' : 'Sans rapport'}`,
          `- Grossesse : ${form.grossesse === 'oui' ? 'En rapport' : 'Sans rapport'}`,
          `- Sorties autorisées : ${form.sortiesLimite === 'oui' ? 'Oui' : 'Non'}`,
          form.sortiesoui ? `- Sorties autorisées à partir du ${form.sortiesoui}` : '',
          `- Sorties autorisées sans restriction d'horaire : ${form.sortiesLibre === 'oui' ? 'Oui' : 'Non'}`,
          form.sortiesLibreDate ? `- Sorties autorisées sans restriction à partir du ${form.sortiesLibreDate}` : '',
          `- Motif médical : ${form.motif || 'Non renseigné'}`,
          `\n ** Section Identification : ** `,
          `- Identification du praticien : ${form.doc}, RPPS : ${form.rpps}`,
          `- Identification de la structure : ${form.idStructure}, SIRET : ${form.numStructure || 'Non renseigné'}`,
          `- Date du début de l'arrêt: ${form.dateDuJour}`,
        ]
        .filter(Boolean)
        .join('\n');

        await sendSecretaryToDo({ comment: recap, urgence: false });
        alert(`Données transmises au secrétariat sous forme de To Do ! \nMerci d'aller signer l'arrêt de travail dans quelques minutes.`);
      }
      navigate(`/fiche/${id}`);
    } catch (err) {
      console.error(err);
      alert(`Erreur : ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !paramsAT) {
    return (
      <PageLayout title="Arrêt de travail">
        <div className="p-6 text-slate-600">Chargement…</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={mode === 'pdf' ? "Génération de l'arrêt de travail" : 'Fiche Arrêt Papier'}>
      <div className="w-full mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            type="button"
            onClick={() => navigate(`/arret-travail/${id}`)}
            className="p-2 rounded-lg hover:bg-slate-200 text-slate-700 bg-slate-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-800">
              {mode === 'pdf' ? 'Génération Arrêt de travail' : 'Saisie pour Arrêt Papier'}
            </h1>
            <p className="text-xs text-slate-500 font-medium">
              Patient : {patient?.civilite} {String(patient?.nom || '').toUpperCase()} {patient?.prenom || ''}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3">
            <label className="block text-sm font-semibold text-slate-700">L'assuré(e)</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Nom</label>
                <input
                  type="text"
                  name="nom"
                  value={form.nom}
                  onChange={handleChange}
                  required
                  className="w-full border rounded-lg p-2 text-sm border-slate-300 uppercase"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Prénom</label>
                <input
                  type="text"
                  name="prenom"
                  value={form.prenom}
                  onChange={handleChange}
                  required
                  className="w-full border rounded-lg p-2 text-sm border-slate-300"
                />
              </div>
            </div>
            
            <div className="relative">
              <label className="block text-xs font-medium text-slate-500 mb-1">Motif d'utilisation d'un arrêt non télétransmis</label>
              <input
                type="text"
                name="motifNoTeletrans"
                value={form.motifNoTeletrans}
                onChange={(e) => {
                  handleChange(e);
                  setShowMotifsNoTeletrans(true);
                }}
                onFocus={() => setShowMotifsNoTeletrans(true)}
                onBlur={() => setTimeout(() => setShowMotifsNoTeletrans(false), 200)}
                required
                placeholder="Sélectionner ou saisir un motif"
                autoComplete="off"
                className="w-full border rounded-lg p-2 text-sm border-slate-300 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#B3000A]"
              />

              {showMotifsNoTeletrans && (
                <ul className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {MOTIFS_NO_TELETRANS_LIST
                    .filter(m => m.toLowerCase().includes((form.motifNoTeletrans || '').toLowerCase()))
                    .map((m, idx) => (
                      <li
                        key={idx}
                        onClick={() => {
                          setForm(prev => ({ ...prev, motifNoTeletrans: m }));
                          setShowMotifsNoTeletrans(false);
                        }}
                        className="px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0 transition-colors"
                      >
                        {m}
                      </li>
                    ))}
                  {MOTIFS_NO_TELETRANS_LIST.filter(m => m.toLowerCase().includes((form.motifNoTeletrans || '').toLowerCase())).length === 0 && (
                    <li className="px-4 py-2.5 text-sm text-slate-500 italic">
                      Motif personnalisé...
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <label className="block text-sm font-semibold text-slate-700 mb-2">Type d'arrêt</label>
            <div className="flex gap-4">
              {['initial', 'prolongation'].map((val) => (
                <label key={val} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="radio"
                    name="typeArret"
                    value={val}
                    checked={form.typeArret === val}
                    onChange={handleChange}
                    className="accent-[#B3000A]"
                  />
                  {val === 'initial' ? 'Initial' : 'Prolongation'}
                </label>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3">
            <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">{mode === 'pdf' ? "Date du jour" : "Date de début de l'arrêt"}</label>
                <input
                  type="date"
                  name="dateDuJour"
                  value={form.dateDuJour}
                  onChange={handleChange}
                  required
                  className="w-full border rounded-lg p-2 text-sm border-slate-300 bg-slate-50"
                />
              </div>
            <div className="relative">
              <label className="block text-sm font-semibold text-slate-700 mb-1">Motif médical de l'arrêt</label>
              <input
                type="text"
                name="motif"
                value={form.motif}
                onChange={(e) => {
                  handleChange(e);
                  setShowMotifs(true);
                }}
                onFocus={() => setShowMotifs(true)}
                onBlur={() => setTimeout(() => setShowMotifs(false), 200)}
                placeholder="Sélectionner ou saisir le motif"
                required
                autoComplete="off"
                className="w-full border rounded-lg p-2.5 text-sm border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#B3000A] bg-white"
              />

              {showMotifs && (
                <ul className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {motifsFiltres.map((m, idx) => (
                    <li
                        key={idx}
                        onClick={() => {
                        setForm(prev => ({ ...prev, motif: m.motif }));
                        setShowMotifs(false);
                        }}
                        className="px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0 transition-colors"
                    >
                        {m.motif}
                    </li>
                    ))}

                    {motifsFiltres.length === 0 && (
                    <li className="px-4 py-2.5 text-sm text-slate-500 italic">
                        Motif personnalisé...
                    </li>
                    )}
                </ul>
                )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1 mt-4">Temps de travail</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="radio"
                    name="nature"
                    value="tempsComplet"
                    checked={form.nature === 'tempsComplet'}
                    onChange={handleChange}
                    className="accent-[#B3000A]"
                  />
                  Temps complet
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="radio"
                    name="nature"
                    value="tempsPartiel"
                    checked={form.nature === 'tempsPartiel'}
                    onChange={handleChange}
                    className="accent-[#B3000A]"
                  />
                  Temps partiel
                </label>
              </div>
            </div>

            {form.nature === 'tempsComplet' ? (
              <div className="space-y-3">
                <div className="mt-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prescrit jusqu'au (inclus)</label>
                  <input
                    type="date"
                    name="dateFin"
                    value={form.dateFin}
                    onChange={handleChange}
                    required
                    className="w-full border rounded-lg p-2 text-sm border-slate-300"
                  />
                </div>

                <div className="flex justify-between items-center text-sm pt-2 border-t border-slate-100">
                  <span>Affection longue durée (ALD)</span>
                  <div className="flex gap-3">
                    {['non', 'oui'].map((v) => (
                      <label key={`ald_${v}`} className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name="affection"
                          value={v}
                          checked={form.affection === v}
                          onChange={handleChange}
                          className="accent-[#B3000A]"
                        />
                        {v.toUpperCase()}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between items-center text-sm">
                  <span>État pathologique lié à une grossesse</span>
                  <div className="flex gap-3">
                    {['non', 'oui'].map((v) => (
                      <label key={`gros_${v}`} className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name="grossesse"
                          value={v}
                          checked={form.grossesse === v}
                          onChange={handleChange}
                          className="accent-[#B3000A]"
                        />
                        {v.toUpperCase()}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Du</label>
                    <input
                      type="date"
                      name="dateDebutTempsPartiel"
                      value={form.dateDebutTempsPartiel}
                      onChange={handleChange}
                      required
                      className="w-full border rounded-lg p-2 text-sm border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Au</label>
                    <input
                      type="date"
                      name="dateFinTempsPartiel"
                      value={form.dateFinTempsPartiel}
                      onChange={handleChange}
                      required
                      className="w-full border rounded-lg p-2 text-sm border-slate-300"
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center text-sm pt-2 border-t border-slate-100">
                  <span>Affection longue durée (ALD)</span>
                  <div className="flex gap-3">
                    {['non', 'oui'].map((v) => (
                      <label key={`ald_tp_${v}`} className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name="affectionTempsPartiel"
                          value={v}
                          checked={form.affectionTempsPartiel === v}
                          onChange={handleChange}
                          className="accent-[#B3000A]"
                        />
                        {v.toUpperCase()}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {form.nature === 'tempsComplet' && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3">
              <label className="block text-sm font-semibold text-slate-700">Autorisations de sortie</label>

              <div className="flex flex-col gap-2 pt-1 text-sm">
                <div className="flex justify-between items-center">
                  <span>Horaires limités (9h-11h / 14h-16h)</span>
                  <div className="flex gap-3">
                    {['non', 'oui'].map((v) => (
                      <label key={`limite_${v}`} className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name="sortiesLimite"
                          value={v}
                          checked={form.sortiesLimite === v}
                          onChange={handleChange}
                          className="accent-[#B3000A]"
                        />
                        {v.toUpperCase()}
                      </label>
                    ))}
                  </div>
                </div>
                {form.sortiesLimite === 'oui' && (
                  <div className="pl-4 pb-2 border-b border-slate-50">
                    <label className="text-xs text-slate-500 mr-2">À partir du :</label>
                    <input
                      type="date"
                      name="sortiesoui"
                      value={form.sortiesoui}
                      onChange={handleChange}
                      required
                      className="border rounded px-2 py-1 text-xs border-slate-300"
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 text-sm pt-1">
                <div className="flex justify-between items-center">
                  <span>Libres (sans restriction d'horaire)</span>
                  <div className="flex gap-3">
                    {['non', 'oui'].map((v) => (
                      <label key={`libre_${v}`} className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name="sortiesLibre"
                          value={v}
                          checked={form.sortiesLibre === v}
                          onChange={handleChange}
                          className="accent-[#B3000A]"
                        />
                        {v.toUpperCase()}
                      </label>
                    ))}
                  </div>
                </div>
                {form.sortiesLibre === 'oui' && (
                  <div className="pl-4">
                    <label className="text-xs text-slate-500 mr-2">À partir du :</label>
                    <input
                      type="date"
                      name="sortiesLibreDate"
                      value={form.sortiesLibreDate}
                      onChange={handleChange}
                      required
                      className="border rounded px-2 py-1 text-xs border-slate-300"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MÉDECIN & STRUCTURE */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-3">
            <label className="block text-sm font-semibold text-slate-700">Le médecin et le centre</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">Médecin prescripteur</label>
                <select
                  name="doc"
                  value={form.doc}
                  onChange={handleChange}
                  required
                  className="w-full border rounded-lg p-2 text-sm border-slate-300 bg-slate-50"
                >
                  <option value="">Sélectionner un médecin</option>
                  {Object.keys(MEDECINS).map(docName => (
                    <option key={docName} value={docName}>{docName}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">Centre</label>
                <select
                  name="idStructure"
                  value={form.idStructure}
                  onChange={handleChange}
                  required
                  className="w-full border rounded-lg p-2 text-sm border-slate-300 bg-slate-50"
                >
                  {Object.keys(STRUCTURES).map((nomStruct) => (
                    <option key={nomStruct} value={nomStruct}>{nomStruct}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 px-4 rounded-xl font-semibold text-white bg-[#B3000A] hover:bg-[#8f0008] transition shadow flex items-center justify-center gap-2 disabled:opacity-50 mt-4"
          >
            {mode === 'pdf' ? (
              <>
                <Download className="w-5 h-5" />
                {submitting ? 'Traitement en cours…' : 'Générer le PDF, préparer le mail et envoyer la To Do'}
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                {submitting ? 'Envoi en cours…' : 'Envoyer la To Do secrétaire'}
              </>
            )}
          </button>
        </form>
      </div>
    </PageLayout>
  );
}